/**
 * useFrameTimeMonitor Hook
 * 
 * Monitors browser frame rendering performance by measuring time between frames.
 * Detects when the browser is struggling to maintain smooth 60fps rendering.
 * 
 * How it works:
 * - Uses requestAnimationFrame to measure time between consecutive frames
 * - 16.67ms per frame = 60fps (ideal)
 * - 33.33ms per frame = 30fps (acceptable)
 * - >50ms per frame = lag/jank detected
 * 
 * Usage:
 * ```tsx
 * const { isLagging, averageFps, frameTime } = useFrameTimeMonitor({
 *   lagThreshold: 50, // ms - frames taking longer indicate lag
 *   smoothThreshold: 20, // ms - frames shorter than this indicate smooth performance
 *   enabled: true
 * });
 * 
 * if (isLagging) {
 *   // Reduce cache size, disable animations, etc.
 * }
 * ```
 * 
 * Performance impact:
 * - Negligible: uses existing requestAnimationFrame loop
 * - No DOM manipulation or heavy calculations
 * - Can be disabled when not needed
 */

import { useState, useEffect, useRef } from 'react';

export interface FrameTimeMonitorOptions {
    /** Frame time threshold (ms) to consider as lagging. Default: 50ms (~20fps) */
    lagThreshold?: number;

    /** Frame time threshold (ms) to consider as smooth. Default: 20ms (50fps+) */
    smoothThreshold?: number;

    /** Whether monitoring is enabled. Default: true */
    enabled?: boolean;

    /** Sample window for calculating average FPS. Default: 60 frames */
    sampleWindow?: number;

    /** Callback when lag state changes */
    onLagStateChange?: (isLagging: boolean) => void;
}

export interface FrameTimeMetrics {
    /** Whether the browser is currently experiencing lag */
    isLagging: boolean;

    /** Average FPS over the sample window */
    averageFps: number;

    /** Most recent frame time in milliseconds */
    frameTime: number;

    /** Number of consecutive lagging frames */
    consecutiveLagFrames: number;

    /** Total frames measured since monitoring started */
    totalFrames: number;
}

export const useFrameTimeMonitor = (options: FrameTimeMonitorOptions = {}): FrameTimeMetrics => {
    const {
        lagThreshold = 50,
        smoothThreshold = 20,
        enabled = true,
        sampleWindow = 60,
        onLagStateChange
    } = options;

    const [metrics, setMetrics] = useState<FrameTimeMetrics>({
        isLagging: false,
        averageFps: 60,
        frameTime: 16.67,
        consecutiveLagFrames: 0,
        totalFrames: 0
    });

    const lastFrameTimeRef = useRef<number>(performance.now());
    const frameTimesRef = useRef<number[]>([]);
    const consecutiveLagFramesRef = useRef(0);
    const totalFramesRef = useRef(0);
    const rafIdRef = useRef<number>();
    const lastLagStateRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            // Clean up when disabled
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
            return;
        }

        const measureFrameTime = (currentTime: number) => {
            const frameTime = currentTime - lastFrameTimeRef.current;
            lastFrameTimeRef.current = currentTime;
            totalFramesRef.current++;

            // Skip first frame (unreliable measurement)
            if (totalFramesRef.current === 1) {
                rafIdRef.current = requestAnimationFrame(measureFrameTime);
                return;
            }

            // Track frame times in rolling window
            frameTimesRef.current.push(frameTime);
            if (frameTimesRef.current.length > sampleWindow) {
                frameTimesRef.current.shift();
            }

            // Calculate average FPS
            const avgFrameTime = frameTimesRef.current.reduce((sum, ft) => sum + ft, 0) / frameTimesRef.current.length;
            const averageFps = Math.round(1000 / avgFrameTime);

            // Detect lag state
            const isLagging = frameTime > lagThreshold;
            const isSmooth = frameTime < smoothThreshold;

            if (isLagging) {
                consecutiveLagFramesRef.current++;
            } else if (isSmooth) {
                consecutiveLagFramesRef.current = 0;
            }

            // Determine overall lag state (require 3 consecutive lag frames to trigger)
            const shouldBeLagging = consecutiveLagFramesRef.current >= 3;

            // Update state
            setMetrics({
                isLagging: shouldBeLagging,
                averageFps,
                frameTime,
                consecutiveLagFrames: consecutiveLagFramesRef.current,
                totalFrames: totalFramesRef.current
            });

            // Notify on lag state change
            if (shouldBeLagging !== lastLagStateRef.current) {
                lastLagStateRef.current = shouldBeLagging;
                onLagStateChange?.(shouldBeLagging);
            }

            rafIdRef.current = requestAnimationFrame(measureFrameTime);
        };

        rafIdRef.current = requestAnimationFrame(measureFrameTime);

        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [enabled, lagThreshold, smoothThreshold, sampleWindow, onLagStateChange]);

    return metrics;
};
